import React, { useEffect, useRef, useState } from 'react';
import { Radio, Save, Loader2, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (s) => {
  if (!s || s <= 0) return '0m 00s';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}m ${String(ss).padStart(2, '0')}s`;
};

const OpSecToggle = ({ token }) => {
  const [config, setConfig] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [form, setForm] = useState({ apk_url: '', apk_filename: '', apk_size_mb: '', apk_version: '', sha256: '' });
  const [err, setErr] = useState('');
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/admin/opsec/config`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setConfig(data);
      setSecondsLeft(data.seconds_remaining || 0);
      setForm({
        apk_url: data.apk_url || '',
        apk_filename: data.apk_filename || 'S0PHIA-Ops.apk',
        apk_size_mb: data.apk_size_mb ?? '',
        apk_version: data.apk_version || '',
        sha256: data.sha256 || '',
      });
    } catch (e) { setErr(String(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const t = setInterval(() => setSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (on) => {
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API}/api/admin/opsec/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ on }),
      });
      if (!r.ok) throw new Error('Toggle failed');
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const uploadAPK = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadStatus('Requesting upload URL…');
    setUploadProgress(0);
    setErr('');
    try {
      // 1. Ask backend for a presigned R2 PUT URL
      const presignRes = await fetch(`${API}/api/admin/opsec/presign-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ filename: file.name, content_type: file.type || 'application/vnd.android.package-archive' }),
      });
      const presign = await presignRes.json();
      if (!presignRes.ok) throw new Error(presign.detail || 'Presign failed');

      // 2. Hash the file client-side while the upload runs
      setUploadStatus('Hashing (SHA-256)…');
      const sha256Promise = crypto.subtle
        .digest('SHA-256', await file.arrayBuffer())
        .then((buf) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join(''));

      // 3. Direct PUT to R2 with progress via XHR (fetch has no progress event)
      setUploadStatus('Uploading to R2…');
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presign.upload_url);
        xhr.setRequestHeader('Content-Type', file.type || 'application/vnd.android.package-archive');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`R2 ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('R2 upload failed'));
        xhr.send(file);
      });

      const sha256 = await sha256Promise;
      const sizeMb = (file.size / (1024 * 1024)).toFixed(2);

      // 4. Auto-fill the metadata form so admin only has to hit Save
      const nextForm = {
        ...form,
        apk_url: presign.public_url,
        apk_filename: file.name,
        apk_size_mb: sizeMb,
        sha256,
      };
      setForm(nextForm);

      // 5. Persist to backend so it's live immediately
      setUploadStatus('Saving details…');
      const saveRes = await fetch(`${API}/api/admin/opsec/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          apk_url: presign.public_url,
          apk_filename: file.name,
          apk_size_mb: parseFloat(sizeMb),
          sha256,
        }),
      });
      if (!saveRes.ok) throw new Error('Save failed after upload');
      await load();
      setUploadStatus(`Uploaded ${file.name} (${sizeMb} MB). SHA-256 captured.`);
    } catch (e) {
      setErr(e.message);
      setUploadStatus('');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const saveConfig = async () => {
    setSaving(true); setErr('');
    try {
      const payload = { ...form };
      if (payload.apk_size_mb === '') delete payload.apk_size_mb;
      else payload.apk_size_mb = parseFloat(payload.apk_size_mb);
      const r = await fetch(`${API}/api/admin/opsec/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Save failed');
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const on = !!config?.enabled;
  const window = config?.window_minutes || 60;

  return (
    <div
      data-testid="opsec-toggle-card"
      className={`border rounded-lg p-4 mb-4 ${on ? 'border-emerald-700/70 bg-emerald-950/25' : 'border-zinc-800 bg-zinc-900/40'}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <Radio className={`h-5 w-5 shrink-0 mt-0.5 ${on ? 'text-emerald-400' : 'text-zinc-500'}`} />
          <div className="min-w-0 flex-1">
            <p className="text-white font-black text-sm">Op Sec · S0PHIA Ops download window</p>
            <p className="text-zinc-400 text-xs leading-relaxed mt-0.5">
              Turn on to open a <strong className="text-white">{window}-minute</strong> download window on <span className="font-mono">/opsec</span>. Every click of ON resets the timer.
            </p>
            <p className={`text-[10px] font-black uppercase tracking-[0.25em] mt-2 ${on ? 'text-emerald-300' : 'text-zinc-500'}`}
               data-testid="opsec-toggle-state">
              {on ? `Open - closes in ${fmt(secondsLeft)}` : 'Closed - nobody can download'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => toggle(true)}
            disabled={saving}
            data-testid="opsec-toggle-on"
            className={`inline-flex items-center h-10 px-4 rounded font-black text-xs uppercase tracking-[0.25em] disabled:opacity-50 ${on ? 'bg-emerald-900/60 text-emerald-300 border border-emerald-700/60' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : on ? 'Reset 60m' : 'Open 60m'}
          </button>
          <button
            type="button"
            onClick={() => toggle(false)}
            disabled={saving || !on}
            data-testid="opsec-toggle-off"
            className="inline-flex items-center h-10 px-4 rounded font-black text-xs uppercase tracking-[0.25em] bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white disabled:opacity-40"
          >
            Close now
          </button>
        </div>
      </div>

      {/* Metadata */}
      <details className="mt-4 border-t border-zinc-800 pt-3">
        <summary className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 cursor-pointer">APK details (URL, filename, version, size, SHA-256)</summary>
        {/* Drag-drop / browse APK - auto-fills URL, size, SHA-256 */}
        <div className="mt-3 rounded border border-dashed border-emerald-700/40 bg-emerald-950/20 px-4 py-3">
          <p className="text-emerald-300 text-[10px] font-black uppercase tracking-[0.25em] mb-2">Upload APK to R2</p>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={(e) => uploadAPK(e.target.files?.[0])}
              disabled={uploading}
              data-testid="opsec-apk-file-input"
              className="text-xs text-zinc-300 file:mr-3 file:px-3 file:py-2 file:rounded file:border-0 file:bg-emerald-600 file:text-white file:font-black file:text-[11px] file:uppercase file:tracking-widest hover:file:bg-emerald-500 file:cursor-pointer disabled:opacity-50"
            />
            {uploading && (
              <span className="text-emerald-300 text-xs flex items-center gap-2" data-testid="opsec-upload-status">
                <Upload className="h-3.5 w-3.5 animate-pulse" />
                {uploadStatus} {uploadProgress > 0 && `${uploadProgress}%`}
              </span>
            )}
          </div>
          {!uploading && uploadStatus && (
            <p className="text-emerald-400 text-xs mt-2" data-testid="opsec-upload-done">✓ {uploadStatus}</p>
          )}
          <p className="text-zinc-500 text-[10px] mt-2 leading-relaxed">
            Drag an .apk here or browse. Uploads straight to Cloudflare R2 under <code className="text-zinc-400">opsec/</code>,
            hashes it locally with SHA-256, and auto-fills the fields below. All you do after that is set the version and hit Save.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-3">
          <label className="col-span-2 block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">APK URL</span>
            <input
              type="url"
              value={form.apk_url}
              onChange={(e) => setForm({ ...form, apk_url: e.target.value })}
              data-testid="opsec-config-apk-url"
              placeholder="https://pub-…r2.dev/opsec/S0PHIA-Ops-v1.0.0.apk"
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 rounded px-3 py-2 text-sm text-white outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Filename</span>
            <input type="text" value={form.apk_filename} onChange={(e) => setForm({ ...form, apk_filename: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 rounded px-3 py-2 text-sm text-white outline-none" />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Version</span>
            <input type="text" value={form.apk_version} onChange={(e) => setForm({ ...form, apk_version: e.target.value })}
              placeholder="1.0.0"
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 rounded px-3 py-2 text-sm text-white outline-none" />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Size (MB)</span>
            <input type="number" step="0.1" value={form.apk_size_mb} onChange={(e) => setForm({ ...form, apk_size_mb: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 rounded px-3 py-2 text-sm text-white outline-none" />
          </label>
          <label className="col-span-2 block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">SHA-256 (optional but recommended)</span>
            <input type="text" value={form.sha256} onChange={(e) => setForm({ ...form, sha256: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 rounded px-3 py-2 text-xs font-mono text-white outline-none" />
          </label>
        </div>
        <button
          type="button"
          onClick={saveConfig}
          disabled={saving}
          data-testid="opsec-config-save"
          className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded bg-red-600 hover:bg-red-500 text-white font-black text-[11px] uppercase tracking-[0.25em] disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {saving ? 'Saving…' : 'Save details'}
        </button>
      </details>

      {err && <p className="text-red-400 text-xs mt-3" data-testid="opsec-toggle-error">{err}</p>}
    </div>
  );
};

export default OpSecToggle;
